/**
 * ═══════════════════════════════════════════════════════════════
 * LUVORA HOME — UPSELL POPUP
 * luvora-upsell-popup.js
 *
 * INSTALL: Add  {{ 'luvora-upsell-popup.js' | asset_url | script_tag }}
 * to theme.liquid just before </body>, AFTER the popup snippet.
 *
 * Supports:
 *   • Standard add-to-cart form submit (fetch / XHR / native)
 *   • Shopify AJAX API  cart/add.js
 *   • Sticky add-to-cart bars that use the same form or fetch
 *   • Dawn / Debut / custom themes via configurable selectors
 *
 * Recommendation priority:
 *   1. Shopify product recommendations API  (/recommendations/products.json)
 *   2. Same collection as the added product  (via product JSON endpoint)
 *   3. Fallback collection defined in LUVORA_UPSELL_CONFIG.fallbackCollection
 * ═══════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ── Configuration ─────────────────────────────────────────────
     Override these by adding a <script> BEFORE this file:
       window.LUVORA_UPSELL_CONFIG = { fallbackCollection: 'my-collection' };
  ─────────────────────────────────────────────────────────────── */
  var CFG = Object.assign({
    /* Max upsell products to show (1 or 2) */
    maxProducts: 2,

    /* Shopify collection handle used as last-resort fallback */
    fallbackCollection: 'all',

    /* Session storage key prefix */
    sessionKey: 'lup_shown_',

    /* CSS class selectors to intercept add-to-cart */
    addToCartFormSelectors: [
      'form[action="/cart/add"]',
      '[data-type="add-to-cart-form"]',
      '.product-form',
      '#product-form',
    ],

    /* Additional standalone button selectors (sticky bar, etc.) */
    addToCartBtnSelectors: [
      '[data-add-to-cart]',
      '[data-sticky-add-to-cart]',
      '.sticky-add-to-cart__bar button',
      '.luv-sticky-bar__btn',
    ],

    /* Selector for the add-to-cart button INSIDE a form */
    submitBtnSelector: '[name="add"], [data-add-to-cart-submit]',

    /* Selector for the variant selector inside a form */
    variantSelector: '[name="id"], select[name="id"], input[name="id"]',

    /* Delay (ms) before opening popup after successful add */
    openDelay: 320,

    /* How many recommendations to request from Shopify API */
    recsLimit: 6,

    /* Shop URL (auto-detected from window.location) */
    shopUrl: window.location.origin,
  }, window.LUVORA_UPSELL_CONFIG || {});


  /* ── DOM refs ────────────────────────────────────────────────── */
  var overlay   = document.getElementById('lup-overlay');
  var dialog    = document.getElementById('lup-dialog');
  var closeBtn  = document.getElementById('lup-close');
  var dismissBtn= document.getElementById('lup-dismiss');
  var productsEl= document.getElementById('lup-products');
  var cardTpl   = document.getElementById('lup-card-tpl');

  if (!overlay || !dialog || !productsEl || !cardTpl) {
    /* Popup HTML not present on this page — bail silently */
    return;
  }


  /* ══════════════════════════════════════════════════════════════
     1. OPEN / CLOSE
     ══════════════════════════════════════════════════════════════ */

  var isOpen = false;

  function openPopup() {
    dialog.hidden   = false;
    isOpen          = true;
    /* Trigger animation on next paint */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        dialog.classList.add('is-open');
        dialog.setAttribute('aria-hidden', 'false');
        /* Move focus into dialog for accessibility */
        var firstFocusable = dialog.querySelector('button, a, [tabindex="0"]');
        if (firstFocusable) firstFocusable.focus();
      });
    });
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
  }

  function closePopup() {
    if (!isOpen) return;
    overlay.classList.remove('is-open');
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    isOpen = false;
    document.removeEventListener('keydown', onKeyDown);
    /* Wait for CSS transition before hiding */
    dialog.addEventListener('transitionend', function onEnd(e) {
      if (e.propertyName !== 'opacity') return;
      dialog.hidden = true;
      dialog.removeEventListener('transitionend', onEnd);
    });
    document.body.style.overflow = '';
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') closePopup();
    /* Focus trap */
    if (e.key === 'Tab') trapFocus(e);
  }

  function trapFocus(e) {
    var focusable = Array.from(dialog.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]'
    ));
    if (!focusable.length) return;
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }

  /* Dismiss triggers */
  if (closeBtn)   closeBtn.addEventListener('click',   closePopup);
  if (dismissBtn) dismissBtn.addEventListener('click',  closePopup);
  overlay.addEventListener('click', closePopup);


  /* ══════════════════════════════════════════════════════════════
     2. SESSION GUARD
     Show popup max once per product per session
     ══════════════════════════════════════════════════════════════ */

  function wasShown(productId) {
    try {
      return !!sessionStorage.getItem(CFG.sessionKey + productId);
    } catch (e) { return false; }
  }

  function markShown(productId) {
    try {
      sessionStorage.setItem(CFG.sessionKey + productId, '1');
    } catch (e) {}
  }


  /* ══════════════════════════════════════════════════════════════
     3. MONEY FORMATTER
     ══════════════════════════════════════════════════════════════ */

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol) || '$';
    return sym + amount;
  }


  /* ══════════════════════════════════════════════════════════════
     4. FETCH UPSELL PRODUCTS
     Priority: Shopify recommendations → same collection → fallback collection
     ══════════════════════════════════════════════════════════════ */

  function fetchUpsellProducts(addedProductId, callback) {
    /* Step 1: try Shopify product_recommendations */
    var recsUrl =
      CFG.shopUrl +
      '/recommendations/products.json' +
      '?product_id=' + addedProductId +
      '&limit=' + CFG.recsLimit;

    fetch(recsUrl)
      .then(function (r) { return r.ok ? r.json() : Promise.reject('recs-failed'); })
      .then(function (data) {
        var products = (data.products || [])
          .filter(function (p) { return p.id !== addedProductId && p.available; });

        if (products.length >= 1) {
          callback(products.slice(0, CFG.maxProducts));
          return;
        }
        /* Fall through to same-collection strategy */
        throw new Error('no-recs');
      })
      .catch(function () {
        /* Step 2: try same-collection products via product JSON */
        fetchSameCollectionFallback(addedProductId, callback);
      });
  }

  function fetchSameCollectionFallback(addedProductId, callback) {
    /* Fetch product JSON to discover its collections */
    fetch(CFG.shopUrl + '/products/' + addedProductId + '.json?fields=id,collections')
      .then(function (r) { return r.ok ? r.json() : Promise.reject('product-failed'); })
      .then(function (data) {
        var cols = (data.product && data.product.collections) || [];
        if (cols.length === 0) throw new Error('no-collections');
        /* Take the first collection handle and fetch its products */
        return fetchCollectionProducts(cols[0].handle, addedProductId, callback);
      })
      .catch(function () {
        /* Step 3: fallback collection defined in config */
        fetchCollectionProducts(CFG.fallbackCollection, addedProductId, callback);
      });
  }

  function fetchCollectionProducts(collectionHandle, excludeId, callback) {
    var url = CFG.shopUrl + '/collections/' + collectionHandle + '/products.json?limit=12';
    fetch(url)
      .then(function (r) { return r.ok ? r.json() : Promise.reject('col-failed'); })
      .then(function (data) {
        var products = (data.products || [])
          .filter(function (p) { return p.id !== excludeId && p.available; });
        callback(products.slice(0, CFG.maxProducts));
      })
      .catch(function () {
        /* Nothing found — show popup with no products (header only) */
        callback([]);
      });
  }


  /* ══════════════════════════════════════════════════════════════
     5. RENDER CARDS
     ══════════════════════════════════════════════════════════════ */

  function renderCards(products) {
    productsEl.innerHTML = '';

    if (!products || products.length === 0) {
      /* Hide the products section entirely — popup still shows header + footer */
      productsEl.style.display = 'none';
      return;
    }
    productsEl.style.display = '';

    products.forEach(function (product) {
      var card = buildCard(product);
      if (card) productsEl.appendChild(card);
    });
  }

  function buildCard(product) {
    var clone = cardTpl.content.cloneNode(true);
    var el    = clone.querySelector('.lup-product-card');
    if (!el) return null;

    /* Resolve first available variant */
    var variant = (product.variants || []).find(function (v) { return v.available; })
      || (product.variants || [])[0]
      || {};

    /* Image */
    var imgLinks = el.querySelectorAll('a.lup-card-img-wrap, a.lup-card-name-link');
    var imgs     = el.querySelectorAll('img');
    imgLinks.forEach(function (a) { a.href = product.url || '/products/' + product.handle; });
    imgs.forEach(function (img) {
      var src = product.featured_image
        ? (product.featured_image.url || product.featured_image).replace(/\.(jpg|jpeg|png|webp|gif)/i, '_480x480.$1')
        : '';
      img.src = src;
      img.alt = product.title;
    });

    /* Category */
    var typeEl = el.querySelector('.lup-card-type');
    if (typeEl) typeEl.textContent = product.product_type || '';

    /* Name */
    var nameEl = el.querySelector('.lup-card-name');
    if (nameEl) nameEl.textContent = product.title;

    /* Benefit / description */
    var benefitEl = el.querySelector('.lup-card-benefit');
    if (benefitEl) {
      var desc = product.body_html
        ? product.body_html.replace(/<[^>]+>/g, '').trim().slice(0, 72)
        : '';
      if (desc) {
        benefitEl.textContent = desc;
        benefitEl.removeAttribute('hidden');
      } else {
        benefitEl.hidden = true;
      }
    }

    /* Price */
    var priceEl   = el.querySelector('.lup-card-price');
    var compareEl = el.querySelector('.lup-card-compare');
    var price     = variant.price || product.price || 0;
    var compare   = variant.compare_at_price || product.compare_at_price || 0;
    if (priceEl) priceEl.textContent = formatMoney(price);
    if (compareEl) {
      if (compare > price) {
        compareEl.textContent = formatMoney(compare);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.hidden = true;
      }
    }

    /* Add button */
    var addBtn = el.querySelector('.lup-add-btn');
    if (addBtn) {
      addBtn.dataset.productId = product.id;
      addBtn.dataset.variantId = variant.id;
      addBtn.addEventListener('click', onAddUpsellClick);
    }

    return el;
  }


  /* ══════════════════════════════════════════════════════════════
     6. ADD UPSELL TO CART (Ajax)
     ══════════════════════════════════════════════════════════════ */

  function onAddUpsellClick() {
    var btn       = this;
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    /* Loading state */
    btn.classList.add('is-loading');
    var originalHtml = btn.innerHTML;
    btn.innerHTML =
      '<svg class="lup-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
    .then(function () {
      /* Success state — update button */
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="20,6 9,17 4,12"/></svg>' +
        ' Added';

      /* Update cart count in header if theme exposes it */
      refreshCartCount();

      /* Auto-close popup after short pause */
      setTimeout(closePopup, 1400);
    })
    .catch(function () {
      btn.classList.remove('is-loading');
      btn.innerHTML = originalHtml;
      btn.addEventListener('click', onAddUpsellClick);
    });
  }

  /* Attempt to refresh cart bubble / count */
  function refreshCartCount() {
    /* Dawn / Sense themes */
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    /* Broadcast for themes that listen on window */
    window.dispatchEvent(new CustomEvent('cart:updated'));
    /* Re-fetch cart JSON and update known count elements */
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count || 0;
        var countEls = document.querySelectorAll(
          '.cart-count, .cart-count-bubble, [data-cart-count], .header__cart-count'
        );
        countEls.forEach(function (el) {
          el.textContent = count;
          if (count > 0) el.removeAttribute('hidden');
        });
      })
      .catch(function () {});
  }


  /* ══════════════════════════════════════════════════════════════
     7. MAIN TRIGGER — intercept add-to-cart
     ══════════════════════════════════════════════════════════════ */

  /* The trigger fires after any successful add-to-cart.
     We use three strategies:

     A) Intercept native form submit (works on all themes)
     B) Patch window.fetch to catch cart/add.js requests
     C) Listen for custom events emitted by popular themes
  */

  var pendingProductId = null;

  /* ── A: Form submit interception ── */
  function attachFormListeners() {
    var selectors = CFG.addToCartFormSelectors.join(', ');
    var forms = document.querySelectorAll(selectors);

    forms.forEach(function (form) {
      if (form.dataset.lupAttached) return;
      form.dataset.lupAttached = '1';

      form.addEventListener('submit', function (e) {
        var variantInput = form.querySelector(CFG.variantSelector);
        if (variantInput) pendingProductId = getProductIdFromVariant(variantInput.value);
      }, true); /* capture phase — before theme JS */
    });
  }

  /* ── B: Patch fetch for cart/add.js ── */
  (function patchFetch() {
    var _originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input.url || '');

      /* Not a cart/add request — pass through */
      if (url.indexOf('/cart/add') === -1) {
        return _originalFetch(input, init);
      }

      /* Extract product / variant from request body */
      if (init && init.body) {
        try {
          var body = JSON.parse(init.body);
          if (body.id) {
            fetch('/variants/' + body.id + '.json')
              .then(function (r) { return r.json(); })
              .then(function (data) {
                if (data.variant) pendingProductId = data.variant.product_id;
              }).catch(function () {});
          }
          if (body.items && body.items[0]) {
            pendingProductId = null; /* multi-item add — skip popup */
          }
        } catch (e) {}
      }

      return _originalFetch(input, init).then(function (response) {
        var cloned = response.clone();
        cloned.json().then(function (data) {
          /* Successful add */
          if (data && (data.id || (data.items && data.items.length))) {
            var pid = pendingProductId || (data.product_id);
            if (pid) {
              setTimeout(function () { triggerPopup(pid); }, CFG.openDelay);
            }
          }
        }).catch(function () {});
        return response;
      });
    };
  })();

  /* ── C: Listen for custom events (Dawn, Hydrogen, etc.) ── */
  document.addEventListener('cart:item-added', function (e) {
    var pid = e.detail && (e.detail.productId || e.detail.product_id);
    if (pid) setTimeout(function () { triggerPopup(pid); }, CFG.openDelay);
  });

  document.addEventListener('product:add-to-cart:success', function (e) {
    var pid = e.detail && e.detail.product && e.detail.product.id;
    if (pid) setTimeout(function () { triggerPopup(pid); }, CFG.openDelay);
  });

  /* Dawn-specific */
  document.addEventListener('cart:update', function () {
    if (pendingProductId) {
      var pid = pendingProductId;
      pendingProductId = null;
      setTimeout(function () { triggerPopup(pid); }, CFG.openDelay);
    }
  });


  /* ══════════════════════════════════════════════════════════════
     8. TRIGGER POPUP
     ══════════════════════════════════════════════════════════════ */

  function triggerPopup(productId) {
    /* Session guard */
    if (wasShown(productId)) return;
    markShown(productId);

    /* Show popup immediately with skeleton state */
    showSkeletons();
    openPopup();

    /* Then fetch and replace with real products */
    fetchUpsellProducts(parseInt(productId, 10), function (products) {
      /* Filter out the just-added product */
      var filtered = products.filter(function (p) { return p.id !== parseInt(productId, 10); });
      renderCards(filtered.slice(0, CFG.maxProducts));
    });
  }

  function showSkeletons() {
    productsEl.innerHTML = '';
    for (var i = 0; i < CFG.maxProducts; i++) {
      var clone = cardTpl.content.cloneNode(true);
      var el    = clone.querySelector('.lup-product-card');
      if (!el) continue;
      el.classList.add('lup-product-card--skeleton');
      el.setAttribute('aria-hidden', 'true');
      productsEl.appendChild(el);
    }
  }


  /* ══════════════════════════════════════════════════════════════
     9. HELPER: GET PRODUCT ID FROM VARIANT ID
     ══════════════════════════════════════════════════════════════ */

  var variantProductCache = {};

  function getProductIdFromVariant(variantId) {
    if (variantProductCache[variantId]) return variantProductCache[variantId];
    /* Look it up asynchronously for next time */
    fetch('/variants/' + variantId + '.json')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.variant) variantProductCache[variantId] = d.variant.product_id;
      }).catch(function () {});
    /* Return null synchronously — popup will still open via event B/C */
    return null;
  }


  /* ══════════════════════════════════════════════════════════════
     10. STANDALONE BUTTON INTERCEPTION
     Sticky add-to-cart bars, fixed buttons, etc.
     ══════════════════════════════════════════════════════════════ */

  function attachStandaloneButtons() {
    var selectors = CFG.addToCartBtnSelectors.join(', ');
    var btns = document.querySelectorAll(selectors);
    btns.forEach(function (btn) {
      if (btn.dataset.lupAttached) return;
      btn.dataset.lupAttached = '1';
      btn.addEventListener('click', function () {
        /* Try to read product ID from a data attribute */
        var pid = btn.dataset.productId || btn.closest('[data-product-id]')
          && btn.closest('[data-product-id]').dataset.productId;
        if (pid) pendingProductId = pid;
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════
     11. SKELETON CSS (injected dynamically — self-contained)
     ══════════════════════════════════════════════════════════════ */

  function injectSkeletonStyles() {
    if (document.getElementById('lup-skeleton-styles')) return;
    var style = document.createElement('style');
    style.id  = 'lup-skeleton-styles';
    style.textContent = [
      '.lup-product-card--skeleton .lup-card-img,',
      '.lup-product-card--skeleton .lup-card-name,',
      '.lup-product-card--skeleton .lup-card-type,',
      '.lup-product-card--skeleton .lup-card-benefit,',
      '.lup-product-card--skeleton .lup-card-price,',
      '.lup-product-card--skeleton .lup-add-btn {',
      '  background: linear-gradient(90deg,',
      '    var(--lup-card, #141011) 0%,',
      '    rgba(255,255,255,.04) 50%,',
      '    var(--lup-card, #141011) 100%',
      '  ) !important;',
      '  background-size: 200% 100% !important;',
      '  animation: lupShimmer 1.6s ease infinite !important;',
      '  color: transparent !important;',
      '  border-color: transparent !important;',
      '  pointer-events: none;',
      '}',
      '.lup-product-card--skeleton .lup-card-name  { height: 1.2rem; width: 70%; }',
      '.lup-product-card--skeleton .lup-card-type  { height: .6rem;  width: 35%; margin-bottom: 10px; }',
      '.lup-product-card--skeleton .lup-card-benefit { height: .76rem; width: 90%; }',
      '.lup-product-card--skeleton .lup-card-price { height: 1.2rem; width: 28%; }',
      '@keyframes lupShimmer {',
      '  0%   { background-position: 200% 0; }',
      '  100% { background-position: -200% 0; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }


  /* ══════════════════════════════════════════════════════════════
     12. INIT
     ══════════════════════════════════════════════════════════════ */

  function init() {
    injectSkeletonStyles();
    attachFormListeners();
    attachStandaloneButtons();

    /* Re-attach after Shopify section / dynamic content loads */
    document.addEventListener('shopify:section:load', function () {
      attachFormListeners();
      attachStandaloneButtons();
    });

    /* Expose API for manual trigger (e.g. from theme JS) */
    window.LuvoraUpsell = {
      open: triggerPopup,
      close: closePopup,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
